import React, { useEffect, useRef, useState } from "react";
import ChatContainer from "@/components/Chat/ChatContainer";
import cls from "./styles.module.scss";

export function HomepageChat() {
  const slotRef = useRef<HTMLDivElement | null>(null);
  const [mobile, setMobile] = useState(window.innerWidth <= 760);
  const [inView, setInView] = useState(false);
  // Opening the page block and opening the fullscreen overlay are different
  // choices. A manual collapse still survives subsequent scrolling.
  const [userChoice, setUserChoice] = useState<
    "collapsed" | "inline" | "fullscreen" | null
  >(null);
  const [hasBeenInline, setHasBeenInline] = useState(false);

  useEffect(() => {
    const query = window.matchMedia("(max-width: 760px)");
    const update = () => setMobile(query.matches);
    query.addEventListener("change", update);
    update();
    return () => query.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (mobile || !slotRef.current) return;
    const slot = slotRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
        if (entry.isIntersecting) setHasBeenInline(true);
      },
      {
        root: slot.closest("[data-site-scroll]"),
        rootMargin: "-80px 0px -100px 0px",
        threshold: 0.01,
      }
    );
    observer.observe(slot);
    return () => observer.disconnect();
  }, [mobile]);

  useEffect(() => {
    if (mobile || !slotRef.current) return;
    const slot = slotRef.current;
    const panel = slot.querySelector<HTMLElement>("[data-chat-expanded]");
    if (!panel) return;
    const measure = () => {
      // Fullscreen is an overlay, not the height of the page's chat block.
      if (panel.dataset.chatLayout === "fullscreen") return;
      const height = Math.ceil(panel.getBoundingClientRect().height);
      if (!height) return;
      slot.style.setProperty("--home-chat-height", `${height}px`);
      if (panel.dataset.chatExpanded === "true")
        slot.style.setProperty("--home-chat-expanded-height", `${height}px`);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(panel);
    return () => observer.disconnect();
  }, [mobile]);

  const docked = !mobile && !inView;
  const fullscreen = !mobile && userChoice === "fullscreen";
  const expanded = fullscreen || (inView && userChoice !== "collapsed");
  // Reserve its measured open height while it floats, not a fixed 560px.
  // A manual collapse immediately releases that space as well.
  const reserveExpanded =
    !mobile && !fullscreen && userChoice !== "collapsed" && hasBeenInline;
  return (
    <div
      ref={slotRef}
      className={cls.slot}
      data-home-chat-slot
      data-chat-reserved={reserveExpanded}
      data-chat-docked={docked}
      data-chat-fullscreen={fullscreen}
    >
      <ChatContainer
        desktopPresentation={
          fullscreen ? "fullscreen" : docked ? "docked" : "inline"
        }
        desktopExpanded={mobile ? undefined : expanded}
        onDesktopExpandedChange={(nextExpanded) =>
          setUserChoice(
            nextExpanded ? (inView ? "inline" : "fullscreen") : "collapsed"
          )
        }
        onDesktopFullscreenRequest={() => setUserChoice("fullscreen")}
        onDesktopFullscreenExit={() =>
          setUserChoice(inView ? "inline" : "collapsed")
        }
      />
    </div>
  );
}
